import {
  GUARD_LINES,
  GUARD_OPTION
} from "@/pages/DeploymentsPage/components/organisms/AgentPlatformTab"
import {
  DEFAULT_VALUES,
  DEPLOYMENT_KEY_FIELD,
  PACKAGE_SPEC_ARCH
} from "@/pages/DeploymentsPage/const"
import { getShellVariableLine } from "@/pages/DeploymentsPage/helpers/deployment-command"

import type {
  AgentPlatform,
  AgentPlatformStep,
  AgentPlatformStepOption
} from "@/pages/DeploymentsPage/components/organisms/AgentPlatformTab"
import type { DeployCommandLine } from "@/pages/DeploymentsPage/helpers/deployment-command.types"

// Every script line stays short enough to fit the command box. A jq program or
// a heredoc spans several groups of their own: a trailing "\" inside the quotes
// would reach jq or the file.

const CONTROL = "aws bedrock-agentcore-control"

const VARIABLE_LINES = {
  runtimeId: getShellVariableLine("RUNTIME_ID", "runtimeId"),
  secretArn: getShellVariableLine("SECRET_ARN", "secretArn"),
  repository: getShellVariableLine("REPOSITORY", "repository"),
  image: getShellVariableLine("IMAGE", "image"),
  agentDir: getShellVariableLine("AGENT_DIR", "agentDir"),
  bucket: getShellVariableLine("BUCKET", "bucket"),
  codeKey: getShellVariableLine("KEY", "codeKey"),
  environment: getShellVariableLine("KODEM_ENVIRONMENT", "environment")
} satisfies Record<string, DeployCommandLine>

export const getDefaultValues = ({
  environment = DEFAULT_VALUES.ENVIRONMENT
}: { environment?: string } = {}) => ({
  key: Date.now().toString(),
  runtimeId: DEFAULT_VALUES.RUNTIME_ID,
  secretArn: DEFAULT_VALUES.SECRET_ARN,
  repository: DEFAULT_VALUES.REPOSITORY,
  image: DEFAULT_VALUES.IMAGE,
  agentDir: DEFAULT_VALUES.AGENT_DIR,
  bucket: DEFAULT_VALUES.BUCKET,
  codeKey: DEFAULT_VALUES.KEY,
  deploymentKey: DEFAULT_VALUES.DEPLOYMENT_KEY,
  environment
})

// Unzips the Kodem package into the root of the agent's code: AgentCore puts
// the root on PYTHONPATH, so Python imports the package's sitecustomize.py and
// starts the sensors without an entry point change. The SHA-256 checksum is the
// package's digest, so one scan covers every session of the package.
const getPackageStep = (version: string): AgentPlatformStep => ({
  prefix: "package",
  title: "agentCore.packageStep",
  lines: {
    zip: {
      text: `ZIP=kortex-${version}-python-${PACKAGE_SPEC_ARCH.ARM64}.zip`
    },
    agentDir: VARIABLE_LINES.agentDir,
    bucket: VARIABLE_LINES.bucket,
    codeKey: VARIABLE_LINES.codeKey,
    unzip: { text: 'unzip -o "$ZIP" -d "$AGENT_DIR"' },
    zipComment: {
      text: "# zip -r keeps the Kodem binaries executable",
      comment: true
    },
    zipAgent: { text: '(cd "$AGENT_DIR" && zip -qr - .) > agent.zip' },
    upload: { text: 'aws s3api put-object --bucket "$BUCKET" --key "$KEY"' },
    uploadBody: { text: "--body agent.zip --checksum-algorithm SHA256" }
  },
  groups: [
    ["zip"],
    ["agentDir"],
    ["bucket"],
    ["codeKey"],
    ["unzip"],
    ["zipComment"],
    ["zipAgent"],
    ["upload", "uploadBody"]
  ]
})

// The launcher reads the deployment key with the runtime's execution role
const STORE_STEP: AgentPlatformStep = {
  prefix: "store",
  title: "agentCore.storeStep",
  lines: {
    create: {
      text: "aws secretsmanager create-secret --name kodem-deployment-key"
    },
    deploymentKey: {
      text: '--secret-string "',
      name: DEPLOYMENT_KEY_FIELD,
      suffix: '"'
    },
    query: { text: "--query ARN --output text" }
  },
  groups: [["create", "deploymentKey", "query"]]
}

const RUNTIME_LINES = {
  runtimeComment: {
    text: "# The runtime's ARN and execution role",
    comment: true
  },
  runtimeArn: { text: `RUNTIME_ARN="$(${CONTROL} get-agent-runtime` },
  runtimeArnId: { text: '--agent-runtime-id "$RUNTIME_ID"' },
  runtimeArnQuery: { text: '--query agentRuntimeArn --output text)"' },
  roleArn: { text: `ROLE_ARN="$(${CONTROL} get-agent-runtime` },
  roleArnId: { text: '--agent-runtime-id "$RUNTIME_ID"' },
  roleArnQuery: { text: '--query roleArn --output text)"' }
} satisfies Record<string, DeployCommandLine>

const RUNTIME_GROUPS: (keyof typeof RUNTIME_LINES)[][] = [
  ["runtimeComment"],
  ["runtimeArn", "runtimeArnId", "runtimeArnQuery"],
  ["roleArn", "roleArnId", "roleArnQuery"]
]

const POLICY_LINES = {
  policyFile: { text: "cat > kodem-policy.json <<EOF" },
  policyStart: { text: '{"Version": "2012-10-17", "Statement": [' },
  inventoryStatement: { text: '{"Effect": "Allow", "Resource": "*",' },
  inventoryRuntimes: {
    text: '"Action": ["bedrock-agentcore:ListAgentRuntimes",'
  },
  inventoryVersions: { text: '"bedrock-agentcore:ListAgentRuntimeVersions",' },
  inventoryEndpoints: {
    text: '"bedrock-agentcore:ListAgentRuntimeEndpoints",'
  },
  // The tags (and owners) of the other runtimes
  inventoryTags: { text: '"bedrock-agentcore:ListTagsForResource"]},' },
  secretStatement: {
    text: '{"Effect": "Allow", "Action": "secretsmanager:GetSecretValue",'
  },
  secretResource: { text: '"Resource": "$SECRET_ARN"},' },
  runtimeStatement: {
    text: '{"Effect": "Allow", "Action": ["bedrock-agentcore:GetAgentRuntime",'
  },
  runtimeTags: { text: '"bedrock-agentcore:ListTagsForResource"],' },
  runtimeResource: { text: '"Resource": "$RUNTIME_ARN"},' }
} satisfies Record<string, DeployCommandLine>

const POLICY_GROUPS: (keyof typeof POLICY_LINES)[][] = [
  ["policyFile"],
  ["policyStart"],
  ["inventoryStatement"],
  ["inventoryRuntimes"],
  ["inventoryVersions"],
  ["inventoryEndpoints"],
  ["inventoryTags"],
  ["secretStatement"],
  ["secretResource"],
  ["runtimeStatement"],
  ["runtimeTags"],
  ["runtimeResource"]
]

const PUT_POLICY_LINES = {
  policyEnd: { text: "EOF" },
  putPolicy: { text: 'aws iam put-role-policy --role-name "${ROLE_ARN##*/}"' },
  // One policy per runtime: runtimes often share an execution role, and a
  // second runtime's grant would otherwise replace the first one's
  putPolicyName: { text: '--policy-name "kodem-$RUNTIME_ID"' },
  putPolicyDocument: { text: "--policy-document file://kodem-policy.json" }
} satisfies Record<string, DeployCommandLine>

const PUT_POLICY_GROUPS: (keyof typeof PUT_POLICY_LINES)[][] = [
  ["policyEnd"],
  ["putPolicy", "putPolicyName", "putPolicyDocument"]
]

// Optional (kortex-deployment agentcore/iam-policy.json
// KodemOptionalRuntimeInventory): the List actions take no runtime ARN, so the
// statement is on "*"; without it Kodem reports this runtime only
const INVENTORY_OPTION: AgentPlatformStepOption = {
  label: "agentCore.inventoryOption",
  description: "agentCore.inventoryOptionDescription",
  lines: [
    "inventoryStatement",
    "inventoryRuntimes",
    "inventoryVersions",
    "inventoryEndpoints",
    "inventoryTags"
  ]
}

// The steps add Kodem to a runtime the user created (create-agent-runtime with
// their own image or code); the subtitle says so before the first runtime read.
// The execution role reads the deployment key, the runtime (its name, version,
// artifact and tags, the tags naming its owner) and the artifact's digest: the
// image manifest, or the code object's checksum. Without the tags or the digest
// the agent is still protected.
const GRANT_STEPS: Record<"container" | "codeUpload", AgentPlatformStep> = {
  container: {
    prefix: "grant",
    title: "agentCore.grantStep",
    subTitle: "agentCore.grantSubtitle",
    option: INVENTORY_OPTION,
    lines: {
      runtimeId: VARIABLE_LINES.runtimeId,
      secretArn: VARIABLE_LINES.secretArn,
      repository: VARIABLE_LINES.repository,
      ...RUNTIME_LINES,
      repositoryArn: {
        text: 'REPOSITORY_ARN="$(aws ecr describe-repositories'
      },
      repositoryArnName: { text: '--repository-names "$REPOSITORY"' },
      repositoryArnQuery: {
        text: "--query 'repositories[0].repositoryArn' --output text)\""
      },
      ...POLICY_LINES,
      imageStatement: {
        text: '{"Effect": "Allow", "Action": "ecr:BatchGetImage",'
      },
      imageResource: { text: '"Resource": "$REPOSITORY_ARN"}]}' },
      ...PUT_POLICY_LINES
    },
    groups: [
      ["runtimeId"],
      ["secretArn"],
      ["repository"],
      ...RUNTIME_GROUPS,
      ["repositoryArn", "repositoryArnName", "repositoryArnQuery"],
      ...POLICY_GROUPS,
      ["imageStatement"],
      ["imageResource"],
      ...PUT_POLICY_GROUPS
    ]
  },
  codeUpload: {
    prefix: "grant",
    title: "agentCore.grantStep",
    subTitle: "agentCore.grantSubtitle",
    option: INVENTORY_OPTION,
    lines: {
      runtimeId: VARIABLE_LINES.runtimeId,
      secretArn: VARIABLE_LINES.secretArn,
      bucket: VARIABLE_LINES.bucket,
      codeKey: VARIABLE_LINES.codeKey,
      ...RUNTIME_LINES,
      ...POLICY_LINES,
      codeStatement: { text: '{"Effect": "Allow",' },
      codeActions: {
        text: '"Action": ["s3:GetObject", "s3:GetObjectVersion"],'
      },
      codeResource: { text: '"Resource": "arn:aws:s3:::$BUCKET/$KEY"}]}' },
      ...PUT_POLICY_LINES
    },
    groups: [
      ["runtimeId"],
      ["secretArn"],
      ["bucket"],
      ["codeKey"],
      ...RUNTIME_GROUPS,
      ...POLICY_GROUPS,
      ["codeStatement"],
      ["codeActions"],
      ["codeResource"],
      ...PUT_POLICY_GROUPS
    ]
  }
}

// update-agent-runtime replaces the runtime's whole configuration: the update
// is the current runtime's settings, with the Kodem environment variables added
// and the new artifact. The settings are an allow-list of the update's fields,
// so a read-only field AWS adds to get-agent-runtime cannot fail the update
const getDeployStep = (
  artifactLines: Record<string, DeployCommandLine>,
  artifactGroups: string[][],
  artifactUpdate: [string, string],
  artifactArgs: string
): AgentPlatformStep => ({
  prefix: "deploy",
  title: "agentCore.deployStep",
  option: GUARD_OPTION,
  lines: {
    runtimeId: VARIABLE_LINES.runtimeId,
    secretArn: VARIABLE_LINES.secretArn,
    ...artifactLines,
    environment: VARIABLE_LINES.environment,
    getComment: {
      text: "# Keep the runtime's settings and environment variables",
      comment: true
    },
    get: { text: `${CONTROL} get-agent-runtime` },
    getArgs: {
      text: '--agent-runtime-id "$RUNTIME_ID" --output json > runtime.json'
    },
    settings: {
      text: 'JQ_UPDATE=\'with_entries(select(.key | IN("agentRuntimeId",'
    },
    settingsArtifact: {
      text: '"agentRuntimeArtifact", "roleArn", "networkConfiguration",'
    },
    settingsAuthorizer: {
      text: '"description", "authorizerConfiguration",'
    },
    settingsProtocol: {
      text: '"requestHeaderConfiguration", "protocolConfiguration",'
    },
    settingsLifecycle: {
      text: '"lifecycleConfiguration", "metadataConfiguration",'
    },
    settingsEnd: {
      text: '"environmentVariables", "filesystemConfigurations"))) |'
    },
    variables: {
      text: ".environmentVariables = ((.environmentVariables // {}) +"
    },
    variablesEnv: { text: "{KODEM_ENVIRONMENT: $env," },
    ...GUARD_LINES,
    variablesKey: { text: "KODEM_DEPLOYMENT_KEY_SECRET_ARN: $secret}) |" },
    artifact: { text: artifactUpdate[0] },
    artifactValue: { text: artifactUpdate[1] },
    jq: {
      text: 'jq --arg env "$KODEM_ENVIRONMENT" --arg secret "$SECRET_ARN"'
    },
    jqArgs: { text: artifactArgs },
    jqFile: { text: '"$JQ_UPDATE" runtime.json > update.json' },
    update: { text: `${CONTROL} update-agent-runtime` },
    updateArgs: { text: "--cli-input-json file://update.json" }
  },
  groups: [
    ["runtimeId"],
    ["secretArn"],
    ...artifactGroups,
    ["environment"],
    ["getComment"],
    ["get", "getArgs"],
    ["settings"],
    ["settingsArtifact"],
    ["settingsAuthorizer"],
    ["settingsProtocol"],
    ["settingsLifecycle"],
    ["settingsEnd"],
    ["variables"],
    ["variablesEnv"],
    ["variablesGuard"],
    ["variablesGuardMode"],
    ["variablesKey"],
    ["artifact"],
    ["artifactValue"],
    ["jq", "jqArgs", "jqFile"],
    ["update", "updateArgs"]
  ]
})

const DEPLOY_STEPS = {
  container: getDeployStep(
    { image: VARIABLE_LINES.image },
    [["image"]],
    [
      ".agentRuntimeArtifact =",
      "{containerConfiguration: {containerUri: $image}}'"
    ],
    '--arg image "$IMAGE"'
  ),
  codeUpload: getDeployStep(
    { bucket: VARIABLE_LINES.bucket, codeKey: VARIABLE_LINES.codeKey },
    [["bucket"], ["codeKey"]],
    [
      ".agentRuntimeArtifact.codeConfiguration.code.s3 =",
      "{bucket: $bucket, prefix: $key}'"
    ],
    '--arg bucket "$BUCKET" --arg key "$KEY"'
  )
}

// Amazon Bedrock AgentCore Runtime runs linux/arm64: the kortex-agentcore image
// for containers, the arm64 Python package for direct code deployment
export const getAgentCorePlatform = (version: string): AgentPlatform => ({
  i18nKey: "agentCore",
  imageName: "kortex-agentcore",
  dockerPlatform: "linux/arm64",
  packageSpecArch: PACKAGE_SPEC_ARCH.ARM64,
  steps: {
    container: [STORE_STEP, GRANT_STEPS.container, DEPLOY_STEPS.container],
    codeUpload: [
      getPackageStep(version),
      STORE_STEP,
      GRANT_STEPS.codeUpload,
      DEPLOY_STEPS.codeUpload
    ]
  },
  getDefaultValues
})
